import { useEffect, useState } from 'react';
import {
  Alert,
  Descriptions,
  Form,
  Input,
  Message,
  Modal,
  Radio,
  Select,
  Spin,
} from '@arco-design/web-react';
import { MoneyInput } from '@/components/ui';
import { billingApi, cardsApi } from '@/api';
import type { FeePreviewResult } from '@/api/cards';
import type { CardBin } from '@/types/card';
import type { BillingProfile } from '@/types/billing';
import { binLimitApplies, dollarsToCents, formatBinLimit, formatMoney, formatPercent } from '@/utils/money';

interface ApplyCardModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormValues {
  bin: string;
  alias: string;
  billingProfileId: string;
  totalLimitDollars: number;
  reloadable: 'yes' | 'no';
}

export default function ApplyCardModal({ visible, onCancel, onSuccess }: ApplyCardModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [bins, setBins] = useState<CardBin[]>([]);
  const [profiles, setProfiles] = useState<BillingProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBin, setSelectedBin] = useState<CardBin | null>(null);
  const [feePreview, setFeePreview] = useState<FeePreviewResult | null>(null);

  const refreshFeePreview = async (dollars: number | undefined) => {
    if (!dollars || dollars <= 0) {
      setFeePreview(null);
      return;
    }
    try {
      const res = await cardsApi.feePreview({
        kind: 'CARD_OPEN',
        amountCents: dollarsToCents(dollars),
      });
      setFeePreview(res);
    } catch {
      setFeePreview(null);
    }
  };

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    Promise.all([cardsApi.listBins(), billingApi.list({ pageSize: 100 })])
      .then(([binList, profilePage]) => {
        setBins(binList);
        setProfiles(profilePage.items);
      })
      .catch(() => {
        /* toasted */
      })
      .finally(() => setLoading(false));
  }, [visible]);

  const handleSubmit = async () => {
    try {
      const v = await form.validate();
      const bin = bins.find((b) => b.bin === v.bin);
      if (!bin) {
        Message.error('请选择有效卡段');
        return;
      }
      const totalLimitCents = dollarsToCents(v.totalLimitDollars);
      if (binLimitApplies(bin.singleLimitCents) && totalLimitCents > bin.singleLimitCents) {
        Message.error(`已超出该卡段单笔最高额度 ${formatBinLimit(bin.singleLimitCents)}`);
        return;
      }
      if (binLimitApplies(bin.totalLimitCents) && totalLimitCents > bin.totalLimitCents) {
        Message.error(`已超出该卡段单卡总限额 ${formatBinLimit(bin.totalLimitCents)}`);
        return;
      }
      setSubmitting(true);
      await cardsApi.create({
        bin: v.bin,
        alias: v.alias,
        billingProfileId: v.billingProfileId,
        totalLimitCents,
        reloadable: v.reloadable === 'yes',
      });
      Message.success('已提交开卡申请，正在等待上游回调');
      onSuccess();
    } catch (e) {
      // 业务错误已由 http 拦截器 toast，避免重复提示
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="申请新虚拟卡"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="提交申请"
      cancelText="取消"
      style={{ width: 520 }}
    >
      <Spin loading={loading} style={{ width: '100%' }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ reloadable: 'no' }}
          onValuesChange={(changes, all) => {
            if ('bin' in changes) {
              const target = bins.find((b) => b.bin === changes.bin);
              setSelectedBin(target ?? null);
            }
            if ('totalLimitDollars' in changes) {
              void refreshFeePreview(all.totalLimitDollars);
            }
          }}
        >
          <Form.Item label="选择卡段 BIN" field="bin" rules={[{ required: true, message: '请选择卡段' }]}>
            <Select placeholder="请选择" showSearch>
              {bins.map((bin) => (
                <Select.Option key={bin.bin} value={bin.bin}>
                  {bin.bin} · {bin.brand} · 单笔上限 {formatBinLimit(bin.singleLimitCents)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedBin ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              content={
                <div>
                  单笔最高 <strong>{formatBinLimit(selectedBin.singleLimitCents)}</strong>，
                  单卡总限额 <strong>{formatBinLimit(selectedBin.totalLimitCents)}</strong>
                  {selectedBin.notice ? <div>风控提示：{selectedBin.notice}</div> : null}
                </div>
              }
            />
          ) : null}

          <Form.Item
            label="卡片别名"
            field="alias"
            rules={[
              { required: true, message: '请输入别名' },
              { maxLength: 32, message: '最多 32 字符' },
            ]}
          >
            <Input placeholder="如：广告投放主卡-Google" allowClear />
          </Form.Item>

          <Form.Item
            label="持卡人模板"
            field="billingProfileId"
            rules={[{ required: true, message: '请选择持卡人' }]}
          >
            <Select placeholder="请选择已登记的持卡人" showSearch>
              {profiles.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName} · {p.country} · {p.email}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="初始限额（USD）"
            field="totalLimitDollars"
            rules={[{ required: true, message: '请输入初始限额' }]}
          >
            <MoneyInput minDollar={1} />
          </Form.Item>

          {feePreview ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              content={
                <Descriptions
                  column={1}
                  size="small"
                  data={[
                    { label: '初始载入', value: formatMoney(feePreview.grossCents) },
                    {
                      label: `入账手续费 (${formatPercent(feePreview.depositFeeRateBps)})`,
                      value: formatMoney(feePreview.feeCents),
                    },
                    { label: '实际到卡', value: formatMoney(feePreview.netCents) },
                    { label: '开卡手续费', value: formatMoney(feePreview.cardOpenFeeCents) },
                    { label: '主账户扣款合计', value: formatMoney(feePreview.walletDebitCents) },
                  ]}
                />
              }
            />
          ) : null}

          <Form.Item label="重复充值" field="reloadable">
            <Radio.Group>
              <Radio value="no">单次卡（用完即弃）</Radio>
              <Radio value="yes">常驻卡（可多次充值）</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
